import { createContext, useContext, useEffect, useMemo, useState } from "react";

const defaultSiteContent = {
  navigation: {
    logoAlt: "شعار كنيسة مار الياس",
    links: [
      { label: "الرئيسية", path: "/" },
      { label: "الأكاليل", path: "/weddings" },
      { label: "أصحاب الأعياد", path: "/feasts" },
      { label: "معلومات الرعية", path: "/information" },
      { label: "تاريخ الكنيسة", path: "/history" },
      { label: "الصور", path: "/gallery" },
      { label: "المكتبة الصوتية", path: "/audio-library" },
      { label: "الفيديوهات والصلوات", path: "/video-prayers" },
      { label: "تواصل معنا", path: "/contact" },
    ],
  },
  hero: {
    headline: "",
    backgroundImage: "",
    primaryButtonLabel: "معرض الصور",
    primaryButtonUrl: "/gallery",
    searchOpenLabel: "تصفّح الأقسام",
    searchCloseLabel: "إغلاق",
    searchSubmitLabel: "فتح",
    searchPlaceholder: "ابحث في أقسام الكنيسة وخدماتها",
  },
  tables: {
    sectionTitle: "أقسام الموقع",
    weddingsTabLabel: "لائحة الأكاليل",
    feastsTabLabel: "أصحاب الأعياد",
    weddingsNameLabel: "اسم العريس",
    feastsNameLabel: "اسم العيد",
    dateLabel: "التاريخ",
    notesLabel: "ملاحظات",
    feastPersonLabel: "صاحب العيد",
    emptyLabel: "لا توجد بيانات حالياً",
    weddingsCardEyebrow: "الأكاليل",
    weddingsCardTitle: "جدول الأكاليل",
    weddingsCardDescription:
      "افتح لائحة الأكاليل الكاملة في صفحة مستقلة لتكون القراءة أوضح وأسهل.",
    weddingsPageButtonLabel: "عرض لائحة الأكاليل",
    feastsCardEyebrow: "الأعياد",
    feastsCardTitle: "لائحة أصحاب الأعياد",
    feastsCardDescription:
      "افتح لائحة أصحاب الأعياد الكاملة في صفحة مستقلة لتكون القراءة أوضح وأسهل.",
    feastsPageButtonLabel: "عرض أصحاب الأعياد",
    historyCardEyebrow: "التاريخ",
    historyCardTitle: "تاريخ الكنيسة",
    historyCardDescription:
      "اطّلع على تاريخ الكنيسة القديمة والجديدة ومراحل البناء والخدمة في البلدة.",
    historyPageButtonLabel: "عرض تاريخ الكنيسة",
    informationCardEyebrow: "المعلومات",
    informationCardTitle: "معلومات الرعية",
    informationCardDescription:
      "اطّلع على الكهنة والراهبات ولجنة الوقف ولائحة البطاركة والبرنامج الأسبوعي المنقول من الموقع القديم.",
    informationPageButtonLabel: "عرض معلومات الرعية",
    galleryCardEyebrow: "الصور",
    galleryCardTitle: "معرض الصور",
    galleryCardDescription:
      "تصفّح مجموعة منتقاة من صور الكنيسة المنقولة من الموقع القديم بعد إعادة تنظيمها.",
    galleryPageButtonLabel: "فتح معرض الصور",
    audioCardEyebrow: "الصوت",
    audioCardTitle: "المكتبة الصوتية",
    audioCardDescription:
      "استمع إلى ملفات صوتية وتراتيل محفوظة من الأرشيف القديم عبر مشغّل واضح داخل الموقع.",
    audioPageButtonLabel: "فتح المكتبة الصوتية",
    contactCardEyebrow: "التواصل",
    contactCardTitle: "تواصل معنا",
    contactCardDescription:
      "صفحة عربية تجمع البريد الإلكتروني وأرقام التواصل الأساسية الخاصة بالرعية.",
    contactPageButtonLabel: "عرض صفحة التواصل",
    feastsBadgeLabel: "قائمة محدّثة",
    weddingsPageEyebrow: "لائحة الأكاليل",
    weddingsPageDescription:
      "توجد لائحة الأكاليل الكاملة في صفحة مستقلة لتكون القراءة أوضح وأسهل على الهاتف والحاسوب.",
    feastsPageEyebrow: "الأعياد",
    feastsPageDescription:
      "توجد لائحة أصحاب الأعياد الكاملة في صفحة مستقلة لتكون القراءة أوضح وأسهل على الهاتف والحاسوب.",
    backHomeLabel: "العودة إلى الرئيسية",
  },
  footer: {
    logoAlt: "شعار كنيسة مار الياس",
    connectTitle: "تواصل معنا",
    links: [
      { label: "صفحة التواصل", url: "/contact" },
      {
        label: "فيسبوك",
        url: "https://www.facebook.com/profile.php?id=100068682005737",
      },
    ],
  },
};

const SiteContentContext = createContext(defaultSiteContent);

function mergeSiteContent(base, incoming = {}) {
  return {
    ...base,
    ...incoming,
    navigation: {
      ...base.navigation,
      ...incoming.navigation,
      links: incoming.navigation?.links ?? base.navigation.links,
    },
    hero: {
      ...base.hero,
      ...incoming.hero,
    },
    tables: {
      ...base.tables,
      ...incoming.tables,
    },
    footer: {
      ...base.footer,
      ...incoming.footer,
      links: incoming.footer?.links ?? base.footer.links,
    },
  };
}

export function SiteContentProvider({ children }) {
  const [siteContent, setSiteContent] = useState(defaultSiteContent);

  useEffect(() => {
    fetch("/data/site-content.json")
      .then((response) => {
        if (!response.ok) {
          throw new Error(`تعذّر تحميل محتوى الموقع: ${response.status}`);
        }

        return response.json();
      })
      .then((json) => {
        setSiteContent(mergeSiteContent(defaultSiteContent, json));
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  const value = useMemo(() => siteContent, [siteContent]);

  return (
    <SiteContentContext.Provider value={value}>
      {children}
    </SiteContentContext.Provider>
  );
}

export function useSiteContent() {
  return useContext(SiteContentContext);
}
